import { useTranslation } from "react-i18next";
import { format } from "date-fns/format";
import { getDateFnsLocale } from "@tellescope/utils";
import { Button } from "@tellescope/ui";
import { Card } from "@tellescope/design-system";
import { ChevronLeft } from "lucide-react";
import type { MetricId } from "./metrics";
import type { useWeightMacroTrackerBlock } from "./useWeightMacroTrackerBlock";
import { useMetricDetailView } from "./useMetricDetailView";
import { useMetricLabel } from "./useMetricLabel";
import { MetricStatHeader } from "./MetricStatHeader";
import { MetricTrendChart } from "./MetricTrendChart";
import { EntryRow } from "./EntryRow";
import { EntryActionsMenu } from "./EntryActionsMenu";
import { DeleteEntryDialog } from "./DeleteEntryDialog";

type TrackerState = ReturnType<typeof useWeightMacroTrackerBlock>;

interface MetricDetailViewProps {
  state: TrackerState;
  metricId: MetricId;
}

export const MetricDetailView = ({ state, metricId }: MetricDetailViewProps) => {
  const { t, i18n } = useTranslation("portal");
  const locale = getDateFnsLocale(i18n.language);
  const { metricLabel } = useMetricLabel();
  const {
    metric,
    weightUnit,
    setWeightUnit,
    series,
    reversedSeries,
    entryRows,
    effectiveDate,
    setSelectedDate,
    selectedPoint,
    prevValue,
    isLatest,
    target,
    deletingEntry,
    setDeletingEntry,
    handleEditWeightPoint,
    handleDeleteWeightPointRequest,
    handleEditEntry,
    handleDeleteEntryRequest,
    handleConfirmDelete,
  } = useMetricDetailView(state, metricId);

  const displayUnit = metric.kind === "weight" ? weightUnit : metric.unit;

  return (
    <div className="space-y-4">
      <Button variant="ghost" size="sm" onClick={state.goToDashboard} className="-ml-2">
        <ChevronLeft className="h-4 w-4 mr-1" />
        {t("Back")}
      </Button>

      {!selectedPoint ? (
        <Card variant="floating" padding="root">
          <Card.Body className="p-6 text-center text-sm text-muted-foreground">
            {t("No {{metric}} logged yet. Add an entry to start building this trend.", {
              metric: metricLabel(metricId).toLowerCase(),
            })}
          </Card.Body>
        </Card>
      ) : (
        <>
          <MetricStatHeader
            date={selectedPoint.date}
            value={selectedPoint.value}
            prevValue={prevValue}
            unit={displayUnit}
            decimals={metric.decimals}
            target={target}
            isLatest={isLatest}
            isWeight={metric.kind === "weight"}
            weightUnit={weightUnit}
            onWeightUnitChange={setWeightUnit}
            metricName={metricLabel(metricId)}
          />

          <MetricTrendChart
            series={series}
            unit={displayUnit}
            name={metricLabel(metricId)}
            target={target}
            selectedDate={effectiveDate ?? undefined}
            onSelectPoint={(point) => setSelectedDate(point.date)}
          />

          <div className="pt-2 border-t">
            <p className="px-1 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t("{{metric}} entries", { metric: metricLabel(metricId) })}
            </p>
            {metric.kind === "weight" ? (
              <div className="divide-y">
                {reversedSeries.map((point, i) => (
                  <EntryRow
                    key={point.date}
                    icon={<span className="w-1.5 h-1.5 rounded-full bg-current" />}
                    title={
                      <>
                        <span className="tabular-nums">{point.value}</span>
                        <span className="text-xs text-muted-foreground font-normal ml-1">{displayUnit}</span>
                        {i === 0 && (
                          <span className="ml-2 text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                            {t("Latest")}
                          </span>
                        )}
                      </>
                    }
                    meta={format(new Date(point.date), "EEE, MMM d, yyyy", { locale })}
                    isSelected={point.date === effectiveDate}
                    onClick={() => setSelectedDate(point.date)}
                    actions={
                      <EntryActionsMenu
                        onEdit={() => handleEditWeightPoint(point.date)}
                        onDelete={() => handleDeleteWeightPointRequest(point.date)}
                      />
                    }
                  />
                ))}
              </div>
            ) : (
              <div className="divide-y">
                {entryRows.map(({ entry, value }, i) => (
                  <EntryRow
                    key={entry.id}
                    icon={<span className="w-1.5 h-1.5 rounded-full bg-current" />}
                    title={
                      <>
                        <span className="tabular-nums">{value}</span>
                        <span className="text-xs text-muted-foreground font-normal ml-1">{displayUnit}</span>
                        {i === 0 && (
                          <span className="ml-2 text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                            {t("Latest")}
                          </span>
                        )}
                      </>
                    }
                    meta={
                      (entry.type === "food"
                        ? (entry.components?.find((c) => c.code.coding)?.code.text ?? t("Meal")) + " · "
                        : "") + format(new Date(entry.timestamp), "EEE, MMM d, yyyy · h:mm a", { locale })
                    }
                    actions={
                      <EntryActionsMenu
                        onEdit={() => handleEditEntry(entry)}
                        onDelete={() => handleDeleteEntryRequest(entry)}
                      />
                    }
                  />
                ))}
              </div>
            )}
          </div>
        </>
      )}

      <DeleteEntryDialog
        entry={deletingEntry}
        onOpenChange={(open) => !open && setDeletingEntry(undefined)}
        onConfirm={handleConfirmDelete}
        isPending={state.isDeleting}
      />
    </div>
  );
};
